/**
 * Route Cost Calculation Utilities
 *
 * The cost formula itself lives in supabase/functions/_shared/routeCost.ts, so the weekly plan
 * snapshot prices a route with the same code as the screens it is a snapshot of. It is
 * re-exported here so callers keep importing from one place.
 */
import { LoadingUnitType, Stop, VehicleType } from "@/types/collections"
import { INTERNAL_STOP_TYPES } from "@/lib/haulageUtils"

import {
  MILES_PER_METER,
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  haversineDistance,
} from "../../supabase/functions/_shared/geo.ts"

export {
  calculateRouteCost,
  findVehicleType,
  priceRoute,
  resolveCrewSize,
  resolveDirectInjectionCost,
  resolveHaulageRevenue,
  sumForecastVolume,
  sumHaulageDeliveryRevenueFromStops,
} from "../../supabase/functions/_shared/routeCost.ts"
export { resolveRouteKind } from "../../supabase/functions/_shared/routeKind.ts"
export { applyPlannedMileageMultiplier, calculateDistanceFromPlannedStops, haversineDistance }
export type {
  CalculateRouteCostParams,
  StopWithDirectInjectionCost,
  StopWithHaulageRevenue,
} from "../../supabase/functions/_shared/routeCost.ts"

// Vehicle capacity calculation

export interface VehicleCapacityInfo {
  vehicleCapacity: {
    eur_pallets: number
    uk_pallets: number
    cages: number
    magnums: number
    sort_bags: number
  }
  usedCapacity: {
    eur_pallets: number
    uk_pallets: number
    cages: number
    magnums: number
    sort_bags: number
  }
  emptyCapacity: {
    eur_pallets: number
    uk_pallets: number
    cages: number
    magnums: number
    sort_bags: number
  }
  capacityAfterStops: {
    eur_pallets: number
    uk_pallets: number
    cages: number
    magnums: number
    sort_bags: number
  }
  totalFillPercentage: number
}

/**
 * The parts of a stop that capacity depends on.
 *
 * Both spellings of the type are accepted on purpose: a `Stop` and a `PlannedStop` carry
 * `slotType`, a `StopInstance` carries `stop_type`. Reading only one would start counting depot
 * and charging stops toward capacity.
 */
export interface CapacityStop {
  slotType?: string
  stop_type?: string
  locationId?: string
  location?: {
    id?: string
    loading_unit_type?: LoadingUnitType
    parcels_per_loading_unit?: number
  } | null
  volumeEstimated?: number | null
  volumeActual?: number | null
  loadingUnitsType?: LoadingUnitType
  loadingUnitsActual?: number | null
  loadingUnitsEstimated?: number | null
}

/** The parts of a site that turn a stop's parcels into loading units. */
type CapacityLocation = {
  id: string
  loading_unit_type?: LoadingUnitType
  parcels_per_loading_unit?: number
}

/**
 * What one stop puts on the vehicle: how many loading units, and of which kind.
 *
 * Nothing for an internal stop, which moves the operation around and carries no volume. On its
 * own so that a figure quoted for a single stop is the one the route's capacity adds up.
 */
export function stopLoadingUnits(
  stop: CapacityStop,
  locations?: CapacityLocation[],
): { units: number; unitType: LoadingUnitType | undefined } {
  const stopType = (stop.stop_type || stop.slotType || "").toLowerCase()
  if (INTERNAL_STOP_TYPES.includes(stopType)) {
    return { units: 0, unitType: undefined }
  }

  const getLocation = (locationId?: string) => {
    if (!locationId || !locations) return null
    return locations.find((loc) => loc.id === locationId)
  }

  // Process collection/delivery stops only
  let loadingUnits = 0
  let loadingUnitType: LoadingUnitType | undefined = stop.loadingUnitsType

  // Use actual loading units if available
  if (
    stop.loadingUnitsActual !== undefined &&
    stop.loadingUnitsActual !== null &&
    stop.loadingUnitsActual > 0
  ) {
    loadingUnits = stop.loadingUnitsActual
    loadingUnitType = stop.loadingUnitsType
  }
  // Otherwise, try to calculate from volume (forecast or actual) and location
  // NOTE: We process ALL stop types (shipper_collection, haulage_collection, depot_injection, etc.)
  // No filtering by stop type - all stops that add capacity should be counted
  else {
    // Try forecast first, then actual if forecast is 0
    const volume =
      stop.volumeEstimated !== undefined &&
      stop.volumeEstimated !== null &&
      stop.volumeEstimated > 0
        ? stop.volumeEstimated
        : stop.volumeActual !== undefined && stop.volumeActual !== null && stop.volumeActual > 0
          ? stop.volumeActual
          : 0

    if (volume > 0) {
      // Try to get location to get loading unit type and parcels per unit
      // First check if location is already joined on the stop (from StopInstance)
      let location = stop.location

      // If not joined, try to get from locations array using locationId
      if (!location) {
        const locationId = stop.locationId || stop.location?.id
        location = locationId ? getLocation(locationId) : null
      }

      if (
        location?.loading_unit_type &&
        location?.parcels_per_loading_unit &&
        location.parcels_per_loading_unit > 0
      ) {
        loadingUnitType = location.loading_unit_type
        loadingUnits = Math.ceil(volume / location.parcels_per_loading_unit)
      }
      // Fallback to stop's loadingUnitsEstimated if available
      else if (
        stop.loadingUnitsEstimated !== undefined &&
        stop.loadingUnitsEstimated !== null &&
        stop.loadingUnitsEstimated > 0
      ) {
        loadingUnits = stop.loadingUnitsEstimated
        // Keep loadingUnitType from stop if available
        if (!loadingUnitType) {
          loadingUnitType = stop.loadingUnitsType
        }
      }
    }
    // Final fallback to loadingUnitsEstimated if we couldn't calculate from volume
    else if (
      stop.loadingUnitsEstimated !== undefined &&
      stop.loadingUnitsEstimated !== null &&
      stop.loadingUnitsEstimated > 0
    ) {
      loadingUnits = stop.loadingUnitsEstimated
      if (!loadingUnitType) {
        loadingUnitType = stop.loadingUnitsType
      }
    }
  }

  return { units: loadingUnits, unitType: loadingUnitType }
}

export function calculateVehicleCapacity(
  vehicleType: VehicleType | null | undefined,
  stops: CapacityStop[],
  locations?: CapacityLocation[],
): VehicleCapacityInfo | null {
  if (!vehicleType) {
    return null
  }

  const vehicleCapacity = {
    eur_pallets: vehicleType.eur_pallet_capacity || 0,
    uk_pallets: vehicleType.uk_pallet_capacity || 0,
    cages: vehicleType.cage_capacity || 0,
    magnums: vehicleType.magnum_capacity || 0,
    sort_bags: vehicleType.sort_bag_capacity || 0,
  }

  const usedCapacity = {
    eur_pallets: 0,
    uk_pallets: 0,
    cages: 0,
    magnums: 0,
    sort_bags: 0,
  }

  // Track cumulative capacity
  const cumulativeUsed = {
    eur_pallets: 0,
    uk_pallets: 0,
    cages: 0,
    magnums: 0,
    sort_bags: 0,
  }

  stops.forEach((stop) => {
    const { units: loadingUnits, unitType: loadingUnitType } = stopLoadingUnits(stop, locations)

    // Calculate cumulative capacity usage for this stop
    const stopCumulativeUsed = { ...cumulativeUsed }

    if (loadingUnits > 0 && loadingUnitType) {
      switch (loadingUnitType) {
        case "Pallets (EUR)":
          cumulativeUsed.eur_pallets += loadingUnits
          stopCumulativeUsed.eur_pallets = cumulativeUsed.eur_pallets
          usedCapacity.eur_pallets += loadingUnits
          break
        case "Pallets (UK)":
          cumulativeUsed.uk_pallets += loadingUnits
          stopCumulativeUsed.uk_pallets = cumulativeUsed.uk_pallets
          usedCapacity.uk_pallets += loadingUnits
          break
        case "Cages":
          cumulativeUsed.cages += loadingUnits
          stopCumulativeUsed.cages = cumulativeUsed.cages
          usedCapacity.cages += loadingUnits
          break
        case "Magnums":
          cumulativeUsed.magnums += loadingUnits
          stopCumulativeUsed.magnums = cumulativeUsed.magnums
          usedCapacity.magnums += loadingUnits
          break
        case "Sort Bags":
          cumulativeUsed.sort_bags += loadingUnits
          stopCumulativeUsed.sort_bags = cumulativeUsed.sort_bags
          usedCapacity.sort_bags += loadingUnits
          break
        case "Loose Load":
          // Loose load doesn't map to a specific capacity type, skip it
          break
      }
    }
  })

  // Calculate empty capacity (initial capacity)
  const emptyCapacity = {
    eur_pallets: vehicleCapacity.eur_pallets,
    uk_pallets: vehicleCapacity.uk_pallets,
    cages: vehicleCapacity.cages,
    magnums: vehicleCapacity.magnums,
    sort_bags: vehicleCapacity.sort_bags,
  }

  // Calculate capacity after stops (remaining capacity)
  const capacityAfterStops = {
    eur_pallets: Math.max(0, vehicleCapacity.eur_pallets - usedCapacity.eur_pallets),
    uk_pallets: Math.max(0, vehicleCapacity.uk_pallets - usedCapacity.uk_pallets),
    cages: Math.max(0, vehicleCapacity.cages - usedCapacity.cages),
    magnums: Math.max(0, vehicleCapacity.magnums - usedCapacity.magnums),
    sort_bags: Math.max(0, vehicleCapacity.sort_bags - usedCapacity.sort_bags),
  }

  // Calculate total fill percentage
  // Sum the fill percentages for each unit type
  // Example: If EUR pallets are 3% full and UK pallets are 4% full, total = 7%
  const fillPercentagesByType: number[] = []

  if (vehicleCapacity.eur_pallets > 0) {
    fillPercentagesByType.push((usedCapacity.eur_pallets / vehicleCapacity.eur_pallets) * 100)
  }
  if (vehicleCapacity.uk_pallets > 0) {
    fillPercentagesByType.push((usedCapacity.uk_pallets / vehicleCapacity.uk_pallets) * 100)
  }
  if (vehicleCapacity.cages > 0) {
    fillPercentagesByType.push((usedCapacity.cages / vehicleCapacity.cages) * 100)
  }
  if (vehicleCapacity.magnums > 0) {
    fillPercentagesByType.push((usedCapacity.magnums / vehicleCapacity.magnums) * 100)
  }
  if (vehicleCapacity.sort_bags > 0) {
    fillPercentagesByType.push((usedCapacity.sort_bags / vehicleCapacity.sort_bags) * 100)
  }

  // Total fill percentage is the sum of all unit type fill percentages
  const totalFillPercentage = fillPercentagesByType.reduce((sum, pct) => sum + pct, 0)

  return {
    vehicleCapacity,
    usedCapacity,
    emptyCapacity,
    capacityAfterStops,
    totalFillPercentage,
  }
}

export interface EmptyLegsDetail {
  count: number
  miles: number
  totalMiles: number | null
  emptyPct: number | null
}

/** What deciding a leg's load needs. Coordinates are only for measuring it. */
export type SegmentStop = {
  slotType?: string
  stop_type?: string
}

type StopForEmptyLegs = SegmentStop & {
  lat: number
  lng: number
}

/**
 * What each leg of a route is carrying.
 *
 * One leg per gap between consecutive stops, so `states[i]` is the run from
 * `stops[i]` to `stops[i + 1]`.
 *
 * - `internal` -- both ends are the operation moving itself around (depot,
 *   charging). Not a leg anyone sells or worries about, and not counted as
 *   empty.
 * - `empty` -- the vehicle is carrying nothing across this leg. These are the
 *   red segments on the timelines and the empty miles in the haulage figures.
 * - `loaded` -- carrying something.
 *
 * A haulage collection running to its own delivery counts as loaded even when
 * the arithmetic says zero: the volume is booked against the delivery, not
 * accumulated from the collection, so the sum understates it.
 *
 * Every surface that colours or counts legs reads them from here; do not write
 * the accumulation out again at a call site.
 */
export type SegmentLoadState = "internal" | "empty" | "loaded"

/** How a leg is drawn on the timelines and maps: dashed grey internal, red empty, grey loaded. */
export const SEGMENT_LINE_STYLE = {
  internal: "dashed_grey",
  empty: "red",
  loaded: "grey",
} as const satisfies Record<SegmentLoadState, string>

export type SegmentLineStyle = (typeof SEGMENT_LINE_STYLE)[SegmentLoadState]

export function segmentLoadStates(
  stops: SegmentStop[],
  getVolumeForStop: (index: number) => number,
): SegmentLoadState[] {
  if (!stops || stops.length < 2) return []

  const typeOf = (stop: SegmentStop | undefined) =>
    (stop?.slotType ?? stop?.stop_type ?? "").toLowerCase()

  const states: SegmentLoadState[] = []
  for (let index = 1; index < stops.length; index++) {
    const prevType = typeOf(stops[index - 1])
    const currType = typeOf(stops[index])

    if (INTERNAL_STOP_TYPES.includes(prevType) && INTERNAL_STOP_TYPES.includes(currType)) {
      states.push("internal")
      continue
    }

    if (
      prevType === "haulage_collection" &&
      (currType === "haulage_delivery" || currType === "delivery")
    ) {
      states.push("loaded")
      continue
    }

    // What is on board by the time the vehicle leaves stop `index - 1`.
    let volumeOnBoard = 0
    for (let i = 0; i < index; i++) {
      const vol = getVolumeForStop(i) || 0
      const slotType = typeOf(stops[i])
      if (slotType === "depot_injection" || slotType === "direct_injection") volumeOnBoard = vol
      else if (slotType === "internal_stop") volumeOnBoard += vol
      else if (slotType === "haulage_delivery" || slotType === "delivery") volumeOnBoard -= vol
      else if (["shipper_collection", "haulage_collection", "collection"].includes(slotType))
        volumeOnBoard += vol
      volumeOnBoard = Math.max(0, volumeOnBoard)
    }

    states.push(volumeOnBoard === 0 ? "empty" : "loaded")
  }
  return states
}

/**
 * Leg states for a monitoring route, read in stop order. Volume is what was collected where known,
 * the forecast otherwise; a stop with no slot type falls back to its coarse collection/delivery type.
 */
export function stopSegmentStates(stops: Stop[]): SegmentLoadState[] {
  const ordered = [...stops].sort((a, b) => a.sequence - b.sequence)
  return segmentLoadStates(
    ordered.map((stop) => ({ slotType: stop.slotType ?? stop.type })),
    (i) => ordered[i].volumeActual ?? ordered[i].volumeEstimated ?? 0,
  )
}

/**
 * Calculate empty legs (same logic as planning list/timeline red segments).
 * getVolumeForStop(index) returns forecast/actual volume for that stop.
 * Returns count, empty miles, total route miles, and empty % of total.
 */
export function calculateEmptyLegsDetailed(
  stops: StopForEmptyLegs[],
  getVolumeForStop: (index: number) => number,
): EmptyLegsDetail {
  if (!stops || stops.length < 2) {
    const totalMiles = calculateDistanceFromPlannedStops(stops)
    return { count: 0, miles: 0, totalMiles: totalMiles ?? null, emptyPct: null }
  }

  let emptyCount = 0
  let emptyMiles = 0

  segmentLoadStates(stops, getVolumeForStop).forEach((state, i) => {
    if (state !== "empty") return
    emptyCount++
    const prevStop = stops[i]
    const currStop = stops[i + 1]
    if (prevStop.lat && prevStop.lng && currStop.lat && currStop.lng) {
      const distMeters = haversineDistance(prevStop.lat, prevStop.lng, currStop.lat, currStop.lng)
      emptyMiles += distMeters * MILES_PER_METER
    }
  })

  const emptyMilesRounded = Math.round(emptyMiles * 100) / 100
  const totalMiles = calculateDistanceFromPlannedStops(stops)
  const emptyPct =
    totalMiles != null && totalMiles > 0
      ? Math.round((emptyMilesRounded / totalMiles) * 1000) / 10
      : null
  return { count: emptyCount, miles: emptyMilesRounded, totalMiles, emptyPct }
}

/**
 * Calculate duration in minutes from start to end time
 * Handles midnight crossover correctly
 */
export function calculateDurationMinutes(startTime: Date, endTime: Date): number | null {
  if (!startTime || !endTime) return null

  const start = new Date(startTime).getTime()
  const end = new Date(endTime).getTime()
  const durationMs = end - start

  if (durationMs < 0) {
    // If end is before start, it might be crossing midnight
    // Add 24 hours to handle overnight routes
    const durationWithMidnight = durationMs + 24 * 60 * 60 * 1000
    if (durationWithMidnight > 0 && durationWithMidnight < 48 * 60 * 60 * 1000) {
      // Reasonable overnight route (less than 48 hours)
      return Math.round(durationWithMidnight / (1000 * 60))
    }
    return null
  }

  return Math.round(durationMs / (1000 * 60))
}

/**
 * Format cost for display (with thousands separators)
 */
export function formatCost(cost: number): string {
  return `£${cost.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Format charging miles breakdown for display (internal vs external)
 */
export function formatChargingMilesBreakdown(cost: {
  charging_internal_miles?: number
  charging_external_miles?: number
}): string | null {
  const internal = cost.charging_internal_miles
  const external = cost.charging_external_miles
  if (internal == null && external == null) return null
  if (internal != null && (external == null || external === 0)) {
    return `${internal.toFixed(1)} mi at internal rate`
  }
  if (internal != null && external != null && external > 0) {
    return `${internal.toFixed(1)} mi internal, ${external.toFixed(1)} mi at external rate`
  }
  return null
}
